import { useSelector } from 'react-redux';
import React, { Fragment, ReactElement, ReactNode } from 'react';
import { Product } from '@portal/portal-core';
import { HollowButton } from 'components/product-buttons';
import { selectLicenseStatus } from '../../../selectors';
import { PRODUCT_INFO } from '../../../constants/products';

import {
  NO_ACTION,
  ACTIVE_INDIVIDUAL_TRIAL,
  EXPIRED_TRIAL,
  ACTIVE_LICENSE,
  ACTIVE_ADMIN,
  INACTIVE_LICENSE,
  EXPIRING_SOON,
} from '../../../constants/product-tab';
import ProductSplash from '../product-splash';

import heroImage from '../images/placeholder-hero.png';

const NEW_WINDOW_ATTRS = {
  rel: 'noopener noreferrer',
  target: '_blank',
};

interface FacsWorkflowManagerTabProps {
  marketingContentOnly?: boolean;
}

interface PageContent {
  header: string;
  description: ReactNode;
  buttons: ReactElement[];
  additionalContent: ReactNode;
}

const FacsWorkflowManagerTab = ({
  marketingContentOnly = false,
}: FacsWorkflowManagerTabProps): ReactElement | null => {
  const { licenseState } = useSelector(selectLicenseStatus);

  const productLinks = {
    customerSupportLink: (
      <a href="mailto:techsupport@flowjo.com" title="FlowJo Customer Support">
        Customer Support
      </a>
    ),
    fjUniversityLink: (
      <a
        {...NEW_WINDOW_ATTRS}
        href="https://www.flowjo.com/learn/flowjo-university"
        title="FlowJo University"
      >
        FlowJo University
      </a>
    ),
    newsletterLink: (
      <a {...NEW_WINDOW_ATTRS} href="https://www.flowjo.com/newsletter" title="FlowJo Newsletter">
        Sign Up
      </a>
    ),
    resourcesLink: (
      <a {...NEW_WINDOW_ATTRS} href="http://info.flowjo.com/resources" title="FlowJo Resources">
        click here
      </a>
    ),
  };

  // TODO: refactor to leverage /constants/
  const productButtons = {
    adminButton: (
      <HollowButton key="admin" href="/admin?product=facsworkflowmanager" text="Go to Admin" />
    ),
  };

  const additionalContent = (() => {
    const blurb1Header = 'placeholder';
    const blurb1Text = 'placeholder';
    const blurb2Header = 'placeholder';
    const blurb2Text = 'placeholder';
    const blurb3Header = 'placeholder';
    const blurb3Text = 'placeholder';

    return {
      blurbs: (
        <Fragment>
          <div>
            <b>{blurb1Header}</b>
            <p>{blurb1Text}</p>
          </div>
          <div>
            <b>{blurb2Header}</b>
            <p>{blurb2Text}</p>
          </div>
          <div>
            <b>{blurb3Header}</b>
            <p>{blurb3Text}</p>
          </div>
        </Fragment>
      ),
      highlights: (
        <Fragment>
          <div>
            <b>placeholder</b>
            <p>placeholder</p>
          </div>
          <div>
            <b>placeholder</b>
            <p>placeholder</p>
            <p>placeholder</p>
          </div>
        </Fragment>
      ),
    };
  })();

  const getPageContent = (): PageContent | null => {
    const { customerSupportLink, fjUniversityLink, resourcesLink } = productLinks;
    const { adminButton } = productButtons;

    const { blurbs, highlights } = additionalContent;

    const headerText = 'placeholder';
    const headerBody = 'placeholder';

    switch (licenseState) {
      case NO_ACTION:
        return {
          header: headerText,
          description: <Fragment>{headerBody}</Fragment>,
          buttons: [],
          additionalContent: blurbs,
        };

      case ACTIVE_INDIVIDUAL_TRIAL:
        return {
          header: 'placeholder',
          description: <Fragment>placeholder {fjUniversityLink}.</Fragment>,
          buttons: [],
          additionalContent: highlights,
        };

      case EXPIRED_TRIAL:
        return {
          header: 'placeholder',
          description: <Fragment>placeholder {customerSupportLink} placeholder.</Fragment>,
          buttons: [],
          additionalContent: blurbs,
        };

      case ACTIVE_LICENSE:
        return {
          header: 'placeholder',
          description: <Fragment>placeholder</Fragment>,
          buttons: [],
          additionalContent: highlights,
        };

      case ACTIVE_ADMIN:
        return {
          header: 'placeholder',
          description: <Fragment>placeholder</Fragment>,
          buttons: [adminButton],
          additionalContent: highlights,
        };

      case INACTIVE_LICENSE:
        return {
          header: 'placeholder',
          description: (
            <Fragment>
              placeholder {customerSupportLink} placeholder, {resourcesLink}!
            </Fragment>
          ),
          buttons: [],
          additionalContent: blurbs,
        };
      case EXPIRING_SOON:
        return {
          header: 'placeholder',
          description: <Fragment>placeholder</Fragment>,
          buttons: [],
          additionalContent: blurbs,
        };
      default:
        return null;
    }
  };

  const pageContent = getPageContent();
  if (!pageContent) {
    return null;
  }
  const facsProductInfo = PRODUCT_INFO[Product.NAMES.facsWorkflowManager];
  return (
    <ProductSplash
      header={pageContent.header}
      description={pageContent.description}
      additionalContent={pageContent.additionalContent}
      heroImg={{ src: heroImage, alt: 'FACS Workflow Manager' }}
      buttons={marketingContentOnly ? null : pageContent.buttons}
      // logoSvg is null for this product and only rendered when buttons are present;
      // cast to string to satisfy ProductSplash's ProductInfo shape without changing the value.
      productInfo={{ name: facsProductInfo.name, logoSvg: facsProductInfo.logoSvg as string }}
    />
  );
};

export default FacsWorkflowManagerTab;
